import type { BillingPlan } from "./types";

/**
 * Subscription statuses that keep the app unlocked. `past_due` stays usable while Stripe retries
 * the card (the parent sees a banner); `canceled`, `unpaid`, `incomplete*` and `paused` lock it.
 */
export const ACTIVE_STATUSES = new Set(["trialing", "active", "past_due"]);

/**
 * Store products. App Store subscriptions have no quantities, so the app sells one flat family plan
 * covering up to maxKids; per-kid pricing is only on the web. Prices live in the stores.
 */
export const APPLE_PRODUCTS: Record<BillingPlan, string> = {
  quarterly: "com.grandirventures.meaningfulrewards.family.quarterly",
  annual: "com.grandirventures.meaningfulrewards.family.annual",
};
/** One Google Play subscription with a base plan per billing period, named after the plan. */
export const GOOGLE_PRODUCT = "family";

/** What the base plan includes; each kid beyond INCLUDED_KIDS is billed per kid. */
export const PLAN_LIMITS = { includedParents: 2, includedKids: 2, maxKids: 12 } as const;

export function hasAccess(state: { exempt: boolean; status: string | null }): boolean {
  return state.exempt || (!!state.status && ACTIVE_STATUSES.has(state.status));
}

/** Kids billed on top of the base plan. */
export function extraKids(kidCount: number): number {
  return Math.max(0, kidCount - PLAN_LIMITS.includedKids);
}

/**
 * Prices are set as a monthly rate (e.g. $3.99) and billed quarterly (3 months up front, which
 * keeps Stripe's per-charge fee small) or annually (10 months' worth: two months free).
 */
export const QUARTER_MONTHS = 3;
export const ANNUAL_MONTHS_CHARGED = 10;
export const quarterlyCents = (monthlyRateCents: number) => monthlyRateCents * QUARTER_MONTHS;
export const defaultAnnualCents = (monthlyRateCents: number) => monthlyRateCents * ANNUAL_MONTHS_CHARGED;

export function formatPrice(cents: number, currency: string): string {
  const whole = cents % 100 === 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

/** Stripe recurring intervals for each plan. */
export const PLAN_INTERVAL: Record<BillingPlan, { interval: "month" | "year"; count: number }> = {
  quarterly: { interval: "month", count: QUARTER_MONTHS },
  annual: { interval: "year", count: 1 },
};

/** How a plan's period reads in the app: "quarter" / "year". */
export const PLAN_PERIOD: Record<BillingPlan, "quarter" | "year"> = { quarterly: "quarter", annual: "year" };

/** A plan price as a monthly rate, rounded to the cent. */
export const perMonth = (cents: number, plan: BillingPlan) => Math.round(cents / (plan === "annual" ? 12 : QUARTER_MONTHS));
